import React from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import { ASSESSMENT_MODULES } from '../../config/assessmentConstants';
import { useNavigate } from 'react-router-dom';
import styles from '../../styles/assessmentFlow.module.css';

const AssessmentResults: React.FC = () => {
  const { state, dispatch, getModuleScore, getTotalScore, getProgramSuggestions } = useAssessment();
  const navigate = useNavigate();
  
  const suggestions = getProgramSuggestions();
  const totalScore = getTotalScore();

  const handleProgramClick = (programSlug: string) => {
    navigate(`/${programSlug}`);
  };

  const handleRestartAssessment = () => {
    // Reset the assessment and go back to welcome screen
    dispatch({ type: 'RESET_ASSESSMENT' });
    dispatch({ type: 'SET_CURRENT_STEP', payload: 'welcome' });
  };

  const handleBrowsePrograms = () => {
    navigate('/discover');
  };

  const getLevelAMessage = () => {
    return "Your answers show patterns that many people address through recovery. There are people just like you that will welcome you and help you recover. See below.";
  };

  const getModuleFeedback = (moduleId: string) => {
    const score = getModuleScore(moduleId);
    const module = ASSESSMENT_MODULES.find(m => m.id === moduleId);
    
    if (!module) return null;
    
    const totalQuestions = module.questions.length;
    const yesAnswers = Object.entries(state.modules[moduleId] || {})
      .filter(([key, value]) => key !== 'yes_count' && value === true)
      .map(([key, value]) => {
        const question = module.questions.find(q => q.id === key);
        return question ? question.text : '';
      })
      .filter(text => text !== '');
    
    return { 
      score, 
      totalQuestions, 
      yesAnswers,
      module 
    };
  };

  const getAllRecommendations = () => {
    const recommendations: NonNullable<ReturnType<typeof getModuleFeedback>>[] = [];
    
    Object.entries(state.modules).forEach(([moduleId, data]) => {
      const score = typeof data.yes_count === 'number' ? data.yes_count : 0;
      if (score > 0) { // Show any program where user answered at least 1 question yes
        const feedback = getModuleFeedback(moduleId);
        if (feedback) {
          recommendations.push(feedback);
        }
      }
    });
    
    // Sort by score (highest first)
    return recommendations.sort((a, b) => b.score - a.score);
  };

  const getPatternsSummary = () => {
    const patterns = [];
    
    if (state.levelA.q1) patterns.push('trying to cut back and not being able to');
    if (state.levelA.q2) patterns.push('continuing despite harm');
    if (state.levelA.q3) patterns.push('secrecy');
    if (state.levelA.q4) patterns.push('preoccupation');
    if (state.levelA.q5) patterns.push('needing more to get the same effect');
    if (state.levelA.q6) patterns.push('using to cope');
    if (state.levelA.q7) patterns.push('harm to relationships, work, or health');
    
    return patterns;
  };

  const recommendations = getAllRecommendations();
  const patterns = getPatternsSummary();

  return (
    <div className={styles.assessmentResults}>
      <div className={styles.resultsHeader}>
        <h1>Your Results</h1>
        <p className={styles.resultsSubtitle}>{getLevelAMessage()}</p>
      </div>

      {/* Start Here Section */}
      {recommendations.length > 0 && (
        <div className={styles.startHereSection}>
          {recommendations.map((recommendation, index) => (
            <div key={index} className={styles.primaryRecommendation}>
              <div className={styles.programHeader}>
                <h3>{recommendation.module.program_name}</h3>
                <span className={styles.fitBadge}>
                  {recommendation.score >= recommendation.totalQuestions * 0.75 ? 'Strong fit' : 
                   recommendation.score >= recommendation.totalQuestions * 0.5 ? 'Moderate fit' : 'Light fit'}
                </span>
              </div>
              
              <div className={styles.whySuggestion}>
                <h4>Why this suggestion:</h4>
                <div className={styles.answerList}>
                  {recommendation.yesAnswers.map((answer: string, answerIndex: number) => (
                    <div key={answerIndex} className={styles.answerItem}>
                      <span className={styles.checkmark}>✓</span>
                      {answer}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className={styles.nextSteps}>
                <h4>Next step</h4>
                <div className={styles.stepsList}>
                  <button 
                    onClick={() => handleProgramClick(recommendation.module.program_slug)}
                    className={styles.primaryAction}
                  >
                    Learn about {recommendation.module.program_name}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Start My Recovery Journey Section */}
      {recommendations.length > 0 && (
        <div className={styles.startJourneySection}>
          <div className={styles.startJourneyContent}>
            <h2>Ready to begin your recovery journey?</h2>
            <p>
              Based on your assessment, we can help you get started with the programs that best fit your needs. 
              Create an account to track your progress and access personalized recovery tools.
            </p>
            <div className={styles.startJourneyActions}>
              <button 
                onClick={() => navigate('/onboarding', { 
                  state: { 
                    preselectedPrograms: recommendations.map(r => r.module.program_slug),
                    fromAssessment: true 
                  }
                })}
                className={styles.startJourneyButton}
              >
                Start My Recovery Journey
              </button>
              <button 
                onClick={() => navigate('/discover')}
                className={styles.exploreMoreButton}
              >
                Explore More Programs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Also Worth Exploring Section */}
      <div className={styles.alsoWorthExploring}>
        <h2>Also worth exploring</h2>
        <div className={styles.exploreOptions}>
          <span>General recovery tools (principles that help across addictions)</span>
          <button 
            onClick={handleBrowsePrograms}
            className={styles.browseButton}
          >
            Browse all programs
          </button>
        </div>
      </div>

      {/* Patterns Section */}
      {patterns.length > 0 && (
        <div className={styles.patternsSection}>
          <h2>Patterns you recognized</h2>
          <p>You reported several common signs of addiction, including {patterns.join(', ')}.</p>
        </div>
      )}

      {/* Notes Section */}
      <div className={styles.notesSection}>
        <div className={styles.notesContent}>
          <div className={styles.disclaimer}>
            <span>This is not a diagnosis or medical advice. Only you can decide what's right for you.</span>
            <span>You're welcome in recovery communities regardless of your answers.</span>
          </div>
          <div className={styles.crisisInfo}>
            <strong>If you're in crisis, call 988 or 1-800-662-HELP (24/7, US).</strong>
          </div>
        </div>
      </div>

      {/* Retake Assessment */}
      <div className={styles.retakeSection}>
        <button
          onClick={handleRestartAssessment}
          className={styles.retakeButton}
          type="button"
        >
          Retake assessment
        </button>
      </div>
    </div>
  );
};

export default AssessmentResults;
